import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Copy, RefreshCw, Ban, FlaskConical, Users } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type AppId =
  | "fryqnc" | "pulsynq" | "mindlynq"
  | "ablelynq" | "deafwav" | "fullynq" | "jadesynq" | "openlynq" | "spiritsynq";

const APP_OPTIONS: AppId[] = [
  "fryqnc", "pulsynq", "mindlynq",
  "ablelynq", "deafwav", "fullynq", "jadesynq", "openlynq", "spiritsynq",
];

const ALACARTE_ITEMS: { key: string; label: string }[] = [
  { key: "amplify", label: "Amplify" },
  { key: "priority_match", label: "Priority Matching" },
  { key: "super_signal", label: "Super Signal" },
  { key: "voice_intro", label: "Voice Intro" },
  { key: "video_intro", label: "Video Intro" },
  { key: "boost_24h", label: "24h Boost" },
  { key: "rewind", label: "Rewind" },
  { key: "advanced_filters", label: "Advanced Filters" },
  { key: "resonate", label: "RESONATE (MindLynq)" },
  { key: "premium_gifts", label: "Premium Gifts" },
  { key: "trusted_share", label: "Trusted Share" },
  { key: "translate", label: "Live Translate" },
  { key: "incognito", label: "Incognito (free)" },
  { key: "venue_unlock", label: "Venue Unlock" },
];

interface CodeRow {
  id: string;
  code: string;
  label: string | null;
  app: AppId;
  granted_tier: string;
  granted_alacarte: string[];
  max_uses: number | null;
  used_count: number;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  notes: string | null;
  is_test: boolean;
  notion_page_id: string | null;
  notion_influencer_name: string | null;
  notion_handle: string | null;
}

interface NotionInfluencer {
  id: string;
  name: string;
  handle: string;
}

const generateCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  const rnd = new Uint32Array(5);
  crypto.getRandomValues(rnd);
  for (const n of rnd) out += chars[n % chars.length];
  return out;
};

const InfluencerCodesPanel = () => {
  const [rows, setRows] = useState<CodeRow[]>([]);
  const [loading, setLoading] = useState(true);

  // form
  const [code, setCode] = useState(generateCode());
  const [app, setApp] = useState<AppId>("fryqnc");
  const [days, setDays] = useState<number>(14);
  const [maxUses, setMaxUses] = useState<string>("1");
  const [items, setItems] = useState<Set<string>>(new Set(ALACARTE_ITEMS.map((i) => i.key)));
  const [isTest, setIsTest] = useState(false);
  const [busy, setBusy] = useState(false);

  // Notion picker
  const [influencers, setInfluencers] = useState<NotionInfluencer[]>([]);
  const [selectedInfluencer, setSelectedInfluencer] = useState<string>("");
  const [loadingInfluencers, setLoadingInfluencers] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("influencer_access_codes")
      .select("id, code, label, app, granted_tier, granted_alacarte, max_uses, used_count, expires_at, revoked_at, created_at, notes, is_test, notion_page_id, notion_influencer_name, notion_handle")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data ?? []) as CodeRow[]);
    setLoading(false);
  };

  const loadInfluencers = async (forApp: AppId) => {
    setLoadingInfluencers(true);
    setInfluencers([]);
    setSelectedInfluencer("");
    try {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        influencers?: NotionInfluencer[];
        error?: string;
      }>("notion-list-influencers", {
        method: "GET" as never, // supabase-js sends as POST by default; use direct fetch instead
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error ?? "load_failed");
      setInfluencers(data.influencers ?? []);
    } catch {
      // Fall back to direct fetch with query string
      try {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notion-list-influencers?app=${forApp}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!json.ok) throw new Error(json.error ?? "load_failed");
        setInfluencers(json.influencers ?? []);
      } catch (e) {
        toast.error((e as Error).message);
      }
    } finally {
      setLoadingInfluencers(false);
    }
  };

  useEffect(() => { void load(); }, []);
  useEffect(() => { void loadInfluencers(app); /* eslint-disable-next-line */ }, [app]);

  const toggleItem = (k: string) => {
    setItems((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const create = async () => {
    const inf = influencers.find((i) => i.id === selectedInfluencer);
    if (!inf) {
      toast.error("Pick an influencer from Notion first");
      return;
    }
    setBusy(true);
    const expiresAt = days > 0 ? new Date(Date.now() + days * 86400_000).toISOString() : null;
    const max = maxUses.trim() === "" ? null : Math.max(1, parseInt(maxUses, 10) || 1);
    const { data, error } = await supabase.functions.invoke<{
      ok: boolean; id?: string; code?: string; error?: string;
    }>("notion-mint-code", {
      body: {
        code: code.toUpperCase().trim(),
        app,
        notion_page_id: inf.id,
        notion_influencer_name: inf.name,
        notion_handle: inf.handle,
        granted_alacarte: Array.from(items),
        max_uses: max,
        expires_at: expiresAt,
        is_test: isTest,
        label: inf.name,
      },
    });
    setBusy(false);
    if (error || !data?.ok) {
      toast.error(error?.message ?? data?.error ?? "mint_failed");
      return;
    }
    toast.success(`Code ${data.code} minted for ${inf.name}`);
    setCode(generateCode());
    void load();
    void loadInfluencers(app);
  };

  const revoke = async (id: string) => {
    const { error } = await supabase
      .from("influencer_access_codes")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Code revoked");
    // Fire-and-forget Notion sync
    supabase.functions.invoke("notion-sync-status", {
      body: { code_id: id, trigger: "revoked" },
    }).catch(() => {/* logged server-side */});
    void load();
  };

  const unrevoke = async (id: string) => {
    const { error } = await supabase
      .from("influencer_access_codes")
      .update({ revoked_at: null })
      .eq("id", id);
    if (error) { toast.error(error.message); return; }
    void load();
  };

  const PARTNER_BASE = "https://wavlynq.com/partner-access";
  const copyLink = (c: string) => {
    void navigator.clipboard.writeText(`${PARTNER_BASE}?code=${c}`);
    toast.success("Partner link copied");
  };
  const copyVendorLink = (c: string) => {
    void navigator.clipboard.writeText(`${PARTNER_BASE}?code=${c}&next=vendor`);
    toast.success("Vendor sandbox link copied");
  };

  const status = (r: CodeRow): { label: string; tone: "default" | "secondary" | "destructive" } => {
    if (r.revoked_at) return { label: "Revoked", tone: "destructive" };
    if (r.expires_at && new Date(r.expires_at) < new Date()) return { label: "Expired", tone: "secondary" };
    if (r.max_uses != null && r.used_count >= r.max_uses) return { label: "Exhausted", tone: "secondary" };
    return { label: "Active", tone: "default" };
  };

  const totals = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => status(r).label === "Active").length,
    redemptions: rows.reduce((s, r) => s + r.used_count, 0),
  }), [rows]);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-4"><p className="text-xs text-muted-foreground">Codes</p><p className="text-2xl font-bold">{totals.total}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Active</p><p className="text-2xl font-bold">{totals.active}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Redemptions</p><p className="text-2xl font-bold">{totals.redemptions}</p></Card>
      </div>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Mint a new access code</h3>
          <Button size="sm" variant="outline" onClick={() => setCode(generateCode())}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="code">Code (5 chars)</Label>
            <Input id="code" value={code} maxLength={8}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="font-mono tracking-[0.3em] uppercase mt-1" />
          </div>
          <div>
            <Label>Drop them into</Label>
            <Select value={app} onValueChange={(v) => setApp(v as AppId)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {APP_OPTIONS.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5" />
              Influencer (from Notion · {app})
            </Label>
            <Select
              value={selectedInfluencer}
              onValueChange={setSelectedInfluencer}
              disabled={loadingInfluencers || influencers.length === 0}
            >
              <SelectTrigger className="mt-1">
                <SelectValue
                  placeholder={
                    loadingInfluencers
                      ? "Loading from Notion…"
                      : influencers.length === 0
                        ? "No unminted influencers for this app"
                        : "Select an influencer"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {influencers.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name}{i.handle ? ` · ${i.handle}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Only shows Notion records where Code Status = "Not Generated" and App matches.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:col-span-2">
            <div>
              <Label htmlFor="days">Active for (days)</Label>
              <Input id="days" type="number" min={0} value={days}
                onChange={(e) => setDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="mt-1" />
              <p className="text-[10px] text-muted-foreground mt-1">0 = no expiry</p>
            </div>
            <div>
              <Label htmlFor="max">Max uses</Label>
              <Input id="max" value={maxUses}
                onChange={(e) => setMaxUses(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="blank = ∞" className="mt-1" />
            </div>
          </div>
        </div>

        <div>
          <Label className="mb-2 block">À la carte permissions</Label>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {ALACARTE_ITEMS.map((it) => (
              <label key={it.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={items.has(it.key)} onCheckedChange={() => toggleItem(it.key)} />
                <span>{it.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setItems(new Set(ALACARTE_ITEMS.map((i) => i.key)))}>All</Button>
            <Button size="sm" variant="ghost" onClick={() => setItems(new Set())}>None</Button>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-md border border-dashed border-border p-3">
          <FlaskConical className="h-4 w-4 mt-0.5 text-primary shrink-0" />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="is-test" className="cursor-pointer">Test code (sandbox demo only)</Label>
              <Switch id="is-test" checked={isTest} onCheckedChange={setIsTest} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Marks redemptions as demo accounts. KYC bypass and Attuned tier
              apply only inside the sandbox profile — never affects real members
              and is easy to wipe later.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={create} disabled={busy || code.length < 4 || !selectedInfluencer} className="w-full sm:w-auto">
            {busy ? "Minting…" : "Mint code"}
          </Button>
        </div>
      </Card>

      <Card className="divide-y">
        {loading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">No codes yet.</div>
        )}
        {rows.map((r) => {
          const st = status(r);
          return (
            <div key={r.id} className="p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-lg font-bold tracking-[0.2em]">{r.code}</span>
                  <Badge variant={st.tone}>{st.label}</Badge>
                  <Badge variant="outline">{r.app}</Badge>
                  <Badge variant="outline">{r.granted_tier.replace("tier_", "")}</Badge>
                  {r.is_test && (
                    <Badge className="bg-amber-500/15 text-amber-600 border border-amber-500/30 hover:bg-amber-500/15">
                      <FlaskConical className="h-3 w-3 mr-1" /> TEST
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {r.notion_influencer_name
                    ? `${r.notion_influencer_name}${r.notion_handle ? ` · ${r.notion_handle}` : ""} · `
                    : r.label ? `${r.label} · ` : ""}
                  {r.used_count}{r.max_uses != null ? `/${r.max_uses}` : "/∞"} used
                  {r.expires_at && ` · expires ${new Date(r.expires_at).toLocaleDateString()}`}
                  {` · ${r.granted_alacarte.length}/14 à la carte`}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => copyLink(r.code)}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Link
                </Button>
                <Button size="sm" variant="outline" onClick={() => copyVendorLink(r.code)}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Vendor link
                </Button>
                {r.revoked_at ? (
                  <Button size="sm" variant="outline" onClick={() => unrevoke(r.id)}>Unrevoke</Button>
                ) : (
                  <Button size="sm" variant="destructive" onClick={() => revoke(r.id)}>
                    <Ban className="h-3.5 w-3.5 mr-1.5" /> Revoke
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
};

export default InfluencerCodesPanel;
